import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Card, CardContent, CardHeader, Divider, IconButton, Modal, Stack, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { ControlledTextInput } from '../../../components/basics/ControlledTextInput';
import { FileProps } from '../../../components/basics/DropzoneComponent';
import { usePostsContext } from '../../../context/PostsProvider';
import { Post } from '../../../models/Post';
import { PostsService } from '../../../services/Posts.service';
import { hideSpinner, showSpinner } from '../../../store/slicers/globalSpinner.slicer';
import { checkResponseStatus } from '../../../utils/api/response';
import { PostScheme } from '../../../utils/forms/validations/formValidations';
import { PostModalAttachments } from '../PostModalAttachments/PostModalAttachments';

interface Props {
  open: boolean;
  onClose: () => void;
  editionData?: Post;
}

export const CreateEditPostModal: React.FC<Props> = ({ open, onClose, editionData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const dispatch = useDispatch();
  const { fetchPosts } = usePostsContext();

  const [uploadedFiles, setUploadedFiles] = useState<Partial<FileProps>[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(PostScheme),
    defaultValues: {
      post: editionData?.descricao
    }
  });

  const handleCreate = async (data: any) => {
    try {
      dispatch(
        showSpinner({
          message: 'Aguarde um momento, publicando...'
        })
      );
      const formData = new FormData();

      formData.append(
        'post',
        JSON.stringify({
          id: editionData?.id || null,
          descricao: data.post
        })
      );

      for (let i = 0; i < uploadedFiles.length; i++) {
        let file = uploadedFiles[i];
        if (file.base64) {
          const blob = await fetch(file.base64).then((res) => res.blob());
          formData.append(`files[${i}]`, blob);
        } else {
          formData.append(`files[${i}]`, file);
        }
      }

      let response = null;

      if (editionData?.id) {
        response = await PostsService.updatePost(formData);
      } else {
        response = await PostsService.createPost(formData);
      }

      if (checkResponseStatus(response)) {
        setUploadedFiles([]);
        reset({ post: '' });
        onClose();
        await fetchPosts();
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(hideSpinner());
    }
  };

  const handleClearFiles = () => {
    setUploadedFiles([]);
  };

  const setUploadedFilesCallback = (files: FileProps[]) => {
    setUploadedFiles([...uploadedFiles, ...files]);
  };

  useEffect(() => {
    if (editionData?.id) {
      reset({
        post: editionData?.descricao
      });

      setUploadedFiles(
        editionData?.arquivos?.map((file: FileProps) => ({
          id: Number(file.id),
          nome: String(file.nome),
          tipo: String(file.tipo),
          base64: 'data:image/png;base64,' + file.base64,
          preview: 'data:image/png;base64,' + file.base64
        })) || []
      );
    }
  }, [editionData]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="new-post-modal-title"
      aria-describedby="new-post-modal-description"
      sx={{
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Card sx={{ width: isMobile ? '100%' : '40%', height: isMobile ? '100%' : 'auto' }}>
        <CardHeader
          title="Criar publicação"
          titleTypographyProps={{
            textAlign: 'center',
            variant: 'h4'
          }}
          action={
            <Tooltip title="Fechar" placement="top">
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          }
        />
        <Divider />
        <CardContent sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Stack spacing={2}>
            <ControlledTextInput
              variant="outlined"
              name="post"
              control={control}
              placeholder="No que você está pensando?"
              multiline
              minRows={3}
              errorMessage={errors.post?.message}
            />
            <PostModalAttachments
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFilesCallback}
              onClearFiles={handleClearFiles}
            />
            <Button onClick={handleSubmit(handleCreate)} fullWidth variant="contained" color="primary">
              Publicar
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Modal>
  );
};
